type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'success';
const logLevels: LogLevel[] = ['debug', 'info', 'warn', 'error', 'success'];

export interface LogMeta {
  [key: string]: unknown;
}

const SERVICE = process.env.NEXT_PUBLIC_DD_SERVICE ?? 'ilotto-web';
const ENV = process.env.NEXT_PUBLIC_ENV ?? process.env.NODE_ENV ?? 'production';
const VERSION = process.env.NEXT_PUBLIC_VERSION ?? '0.0.1';
// Controls whether browser logs are forwarded to Datadog (no effect on server)
const DD_FORWARD_LOGS =
  (process.env.NEXT_PUBLIC_DD_FORWARD_LOGS ?? 'true').toString().toLowerCase() === 'true';
const DEFAULT_LOG_LEVEL: LogLevel =
  (process.env.LOG_LEVEL as LogLevel) &&
  logLevels.includes(process.env.LOG_LEVEL as LogLevel)
    ? (process.env.LOG_LEVEL as LogLevel)
    : 'debug';

const shouldLog = (currentLevel: LogLevel, minLevel: LogLevel): boolean =>
  logLevels.indexOf(currentLevel) >= logLevels.indexOf(minLevel);

const getColorCode = (level: LogLevel): string => {
  const colors: Record<LogLevel, string> = {
    debug: '37',
    info: '34',
    warn: '33',
    error: '31',
    success: '32',
  };
  return colors[level];
};

const formatTimestamp = (): string => {
  const now = new Date();
  return now.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

function getMessage(
  colorCode: string,
  timestamp: string,
  level: LogLevel,
  message: string,
) {
  if (ENV !== 'development') {
    return message;
  }
  return `\x1b[${colorCode}m[${timestamp}] [${level.toUpperCase()}]: ${message}\x1b[0m`;
}

function isDynamicServerUsageError(error: unknown): error is Error & {
  digest: 'DYNAMIC_SERVER_USAGE';
} {
  return (
    error instanceof Error && 'digest' in error && error.digest === 'DYNAMIC_SERVER_USAGE'
  );
}

function getDynamicServerUsageError(meta: unknown[]):
  | (Error & {
      digest: 'DYNAMIC_SERVER_USAGE';
    })
  | undefined {
  const candidate = meta[0];

  if (isDynamicServerUsageError(candidate)) {
    return candidate;
  }

  if (
    candidate &&
    typeof candidate === 'object' &&
    'error' in candidate &&
    isDynamicServerUsageError(candidate.error)
  ) {
    return candidate.error;
  }

  return undefined;
}

function serializeError(value: unknown): unknown {
  if (value instanceof Error) {
    const serialized: Record<string, unknown> = {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
    for (const key of Object.keys(value)) {
      serialized[key] = (value as unknown as Record<string, unknown>)[key];
    }
    return serialized;
  }
  return value;
}

type TraceContextProvider = () => Record<string, unknown> | undefined;

let traceContextProvider: TraceContextProvider | null = null;

/**
 * Lets server bootstrap (instrumentation.ts) attach the active trace ids to
 * server logs without pulling the tracing API into the client bundle.
 */
export function setLogTraceContextProvider(provider: TraceContextProvider | null) {
  traceContextProvider = provider;
}

const getTraceContext = (): Record<string, unknown> => {
  if (!traceContextProvider || typeof window !== 'undefined') return {};
  try {
    return traceContextProvider() ?? {};
  } catch {
    return {};
  }
};

function baseLog(
  level: LogLevel,
  minLevel: LogLevel,
  message: string,
  ...meta: unknown[]
) {
  const dynamicServerUsageError = getDynamicServerUsageError(meta);

  if (dynamicServerUsageError) {
    throw dynamicServerUsageError;
  }

  if (!shouldLog(level, minLevel)) {
    return;
  }

  const timestamp = formatTimestamp();
  const colorCode = getColorCode(level);

  // Fold meta array into a single object for better structured context
  const metaObj: Record<string, unknown> | undefined = (() => {
    if (!meta || meta.length === 0) return undefined;
    if (meta.length === 1) {
      if (meta[0] instanceof Error) {
        return { error: serializeError(meta[0]) as Record<string, unknown> };
      }
      if (typeof meta[0] === 'object' && meta[0] !== null) {
        return meta[0] as Record<string, unknown>;
      }
      return { 0: meta[0] };
    }
    return Object.fromEntries(meta.map((v, i) => [String(i), serializeError(v)]));
  })();

  const payload = {
    timestamp: new Date().toISOString(),
    level,
    message: getMessage(colorCode, timestamp, level, message),
    env: ENV,
    service: SERVICE,
    version: VERSION,
    ...getTraceContext(),
    details: {
      ...metaObj,
    },
  };

  const line =
    ENV === 'development'
      ? payload.message + ' ' + JSON.stringify(metaObj ?? {})
      : JSON.stringify(payload);
  console.log(line);

  // Forward to Datadog Browser Logs on the client (no-op on server)
  if (DD_FORWARD_LOGS && typeof window !== 'undefined') {
    try {
      // Lazy require to avoid SSR importing the browser SDK
      // eslint-disable-next-line @typescript-eslint/no-var-requires,@typescript-eslint/no-require-imports
      const { datadogLogs } = require('@datadog/browser-logs');
      const context = {
        env: ENV,
        service: SERVICE,
        version: VERSION,
        ...(metaObj ?? {}),
      } as Record<string, unknown>;

      const lvl = level === 'success' ? 'info' : level;
      switch (lvl) {
        case 'debug':
          datadogLogs.logger.debug(message, context);
          break;
        case 'info':
          datadogLogs.logger.info(message, context);
          break;
        case 'warn':
          datadogLogs.logger.warn(message, context);
          break;
        case 'error':
          datadogLogs.logger.error(message, context);
          break;
      }
    } catch {
      // ignore if SDK not available or not initialized
    }
  }
}

export function createLogger(
  minLevel: LogLevel = DEFAULT_LOG_LEVEL,
): Record<LogLevel, (msg: string, ...meta: unknown[]) => void> {
  const effectiveLevel = logLevels.includes(minLevel) ? minLevel : 'debug';

  return {
    debug: (msg: string, ...meta: unknown[]) =>
      baseLog('debug', effectiveLevel, msg, ...meta),
    info: (msg: string, ...meta: unknown[]) =>
      baseLog('info', effectiveLevel, msg, ...meta),
    warn: (msg: string, ...meta: unknown[]) =>
      baseLog('warn', effectiveLevel, msg, ...meta),
    error: (msg: string, ...meta: unknown[]) =>
      baseLog('error', effectiveLevel, msg, ...meta),
    success: (msg: string, ...meta: unknown[]) =>
      baseLog('success', effectiveLevel, msg, ...meta),
  };
}

export const logger = createLogger(DEFAULT_LOG_LEVEL);
